import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import { MdLocalPhone, MdPerson, MdHome, MdEmail } from "react-icons/md";
import { LuBuilding } from "react-icons/lu";
import SoftButton from "../../components/SoftButton";
import { IoClose } from "react-icons/io5";
import Divider from '@mui/material/Divider';
import PropTypes from "prop-types";
import {getDepartementsList, updateProfile} from "../../axios-client";
import {LIBRARIAN_ROLE, LIBRARY_ADMIN_ROLE} from "../../utlis/constant";

const EditLibrarianDialog = ({ open, onClose, onLibrarianAdded, librarianData, isSuperAdmin, setSnackbar }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        department: '',
        address: "",
    });
    const [errors, setErrors] = useState({});
    const [departments, setDepartments] = useState([]);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (open && librarianData) {
            setFormData({
                name: librarianData.name || '',
                email: librarianData.email || '',
                phone: librarianData.phone || '',
                department: !isSuperAdmin ? (librarianData.departments ? librarianData.departments.map(department => department.id) : '') : '',
                address: librarianData.address || '',
            });
        }
    }, [open, librarianData, isSuperAdmin]);

    useEffect(() => {
        async function loadDepartments() {
            if (!isSuperAdmin) {
                try {
                    const response = await getDepartementsList()
                    setDepartments(response.data.departments || []);
                } catch (error) {
                    console.error("Failed to load departments", error);
                    setDepartments([]);
                }
            }
        }
        loadDepartments();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validateForm = () => {
        let formErrors = {};
        if (!formData.name) formErrors.name = 'Name is required';
        if (!formData.email) formErrors.email = 'Email is required';
        if (!formData.phone) formErrors.phone = 'Phone is required';
        if (!isSuperAdmin && !formData.department) formErrors.department = 'Department is required';
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailPattern.test(formData.email)) {
            formErrors.email = "Please enter a valid email address";
        }

        return formErrors;
    };

    const handleSubmit = () => {
        const formErrors = validateForm();
        if (Object.keys(formErrors).length === 0) {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('address', formData.address);
            if (!isSuperAdmin) {
                formDataToSend.append('departments', formData.department);
            }
            const role = isSuperAdmin ? LIBRARY_ADMIN_ROLE : LIBRARIAN_ROLE;
            updateProfile(librarianData.id, formDataToSend, role).then(res => {
                onLibrarianAdded();
                setSnackbar({ open: true, message: 'Librarian updated successfully', severity: 'success' });
                onClose();
            }).catch(error => {
                setSnackbar({ open: true, message: 'Failed to update the librarian', severity: 'error' });
            })
        } else {
            setErrors(formErrors);
            setSnackbar({ open: true, message: 'Please fill out all required fields', severity: 'error' });
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const handleSnackbarClose = () => {
        setSnackbar({ open: false, message: '', severity: '' });
    };
    const handleDepartmentChange = (e) => {
        setFormData({ ...formData, department: e.target.value });
    };

    const inputContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${isFocused ? '#cdcecf' : '#dcdfe3'}`,
        borderRadius: '8px',
        padding: '5px 10px',
        marginBottom: '10px',
        transition: 'border-color 0.3s ease',
    };

    const inputStyle = {
        width: '100%',
        padding: '10px 0px',
        border: 'none',
        fontSize: '14px',
        boxSizing: 'border-box',
        outline: 'none',
        fontFamily: 'Arial, Roboto',
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle
                id="form-dialog-title"
                sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
            >
                {isSuperAdmin ? "Update Librarian Admin Information" : "Update Librarian Information"}
                <IoClose
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        size: 10
                    }}
                />
            </DialogTitle>
            <Divider/>
            <DialogContent>
                <label htmlFor="name" className="label-style">Name</label>
                <div style={inputContainerStyle}>
                    <MdPerson className="icon-style"/>
                    <input type="text" id="name" name="name" style={inputStyle}
                           value={formData.name}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.name && <p style={{color: 'red', fontSize: "12px"}}>{errors.name}</p>}

                <label htmlFor="email" className="label-style">Email</label>
                <div style={inputContainerStyle}>
                    <MdEmail className="icon-style"/>
                    <input type="text" id="email" name="email" style={inputStyle}
                           value={formData.email}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.email && <p style={{color: 'red', fontSize: "12px"}}>{errors.email}</p>}

                <label htmlFor="phone" className="label-style">Phone</label>
                <div style={inputContainerStyle}>
                    <MdLocalPhone className="icon-style"/>
                    <input type="text" id="phone" name="phone" style={inputStyle}
                           value={formData.phone}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.phone && <p style={{color: 'red', fontSize: "12px"}}>{errors.phone}</p>}

                {!isSuperAdmin && (
                    <>
                        <label htmlFor="department" className="label-style">Department</label>

                        <SoftBox mb={2} style={{position: 'relative'}}>
                            <LuBuilding className="icon-style" style={{
                                position: 'absolute',
                                left: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                fontSize: '20px',
                                color: '#666'
                            }}/>

                            <select
                                id="department"
                                name="department"
                                value={formData.department}
                                onChange={handleDepartmentChange}
                                style={{
                                    width: "100%",
                                    padding: "10px 10px 10px 40px",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                    background: "transparent",
                                }}
                            >
                                <option disabled={true} value="">
                                    Select a department
                                </option>
                                {departments?.map((department) => (
                                    <option key={department.id} value={department.id}>
                                        {department.name}
                                    </option>
                                ))}
                            </select>
                            {errors.department && (
                                <SoftTypography color="error" fontSize="small">
                                    {errors.department}
                                </SoftTypography>
                            )}
                        </SoftBox>
                    </>
                )}
                <label htmlFor="address" className="label-style">Address</label>
                <div style={inputContainerStyle}>
                    <MdHome className="icon-style"/>
                    <input type="text" id="address" name="address" style={inputStyle}
                           value={formData.address}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.address && <p style={{color: 'red', fontSize: "12px"}}>{errors.address}</p>}

            </DialogContent>

            <DialogActions>
                <SoftButton variant="outlined" color="dark" onClick={handleCancel}>
                    Cancel
                </SoftButton>
                <SoftButton variant="gradient" color="primary" onClick={handleSubmit}>
                    Save
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
};

EditLibrarianDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onLibrarianAdded: PropTypes.func,
    librarianData: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        departments: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
        })),
        address: PropTypes.string,
    }),
    isSuperAdmin: PropTypes.bool.isRequired,
    setSnackbar: PropTypes.func
};

export default EditLibrarianDialog;
